import { AUDIO } from '@config/constants';
import type { TimbrePreset } from './timbres';
import { TIMBRE_PRESETS } from './timbres';

/**
 * A plucked voice rendered from a timbre preset. The preset defines the
 * oscillator pair, ADSR-style envelope, filter shape, and a wet-send
 * amount for effects. Voices always self-terminate within `tailSec + 0.1`.
 */

export class PluckVoice {
  readonly osc1: OscillatorNode;
  readonly osc2: OscillatorNode;
  readonly filter: BiquadFilterNode;
  readonly amp: GainNode;

  private released = false;
  private stopped = false;
  private onEnded?: () => void;

  constructor(
    private readonly ctx: AudioContext,
    private readonly out: AudioNode,
    private baseFreq: number,
    velocity: number,
    /** Optional wet bus; the voice sends `preset.wet` of its output here. */
    wetOut?: AudioNode,
    preset: TimbrePreset = TIMBRE_PRESETS[0]!,
    /** Audio-context start time. If omitted or in the past, the voice
     *  starts immediately. Used by the quantize path so a snapped beat
     *  fires at sample-accurate audio time (matching the metronome's
     *  scheduled clicks) instead of via setTimeout jitter. */
    startTime?: number,
  ) {
    const t =
      startTime !== undefined && startTime > ctx.currentTime
        ? startTime
        : ctx.currentTime;
    const v = Math.max(0, Math.min(1, velocity));
    // Base floor raised from 0.06 → 0.22 so any pluck that fires is
    // clearly audible. The old 0.06 floor meant a shaped-velocity of
    // 0.1 yielded only a 12% peak gain — barely audible on laptop
    // speakers, which the player experienced as "slow plucks not
    // sounding". The 0.22 floor + 0.5 velocity span still keeps
    // dynamic range (min 22%, max 72% of preset gain).
    const peak = (0.22 + 0.5 * v) * preset.peakGainScale;
    const tailSec = preset.tailSec;
    const stopSec = tailSec + 0.1;

    this.osc1 = ctx.createOscillator();
    this.osc2 = ctx.createOscillator();
    this.filter = ctx.createBiquadFilter();
    this.amp = ctx.createGain();

    this.osc1.type = preset.osc1;
    this.osc2.type = preset.osc2;
    this.osc1.frequency.value = baseFreq;
    this.osc2.frequency.value = baseFreq * 1.002;
    this.osc2.detune.value = preset.detuneCents;

    this.filter.type = preset.filterType;
    this.filter.Q.value = preset.filterQ;
    const cutoffPeak = Math.min(
      18000,
      baseFreq * preset.cutoffMult + v * preset.cutoffVelBoost,
    );
    const cutoffTail = Math.max(220, baseFreq * preset.cutoffTailMult);
    this.filter.frequency.setValueAtTime(cutoffPeak, t);
    this.filter.frequency.exponentialRampToValueAtTime(cutoffTail, t + 0.5);

    // Preset-driven envelope: 0 → peak (attack) → peak*sustain (decay) → 0 (tail).
    this.amp.gain.setValueAtTime(0.0001, t);
    this.amp.gain.exponentialRampToValueAtTime(peak, t + preset.attackMs / 1000);
    const sustainGain = Math.max(0.0001, peak * preset.sustainFrac);
    this.amp.gain.exponentialRampToValueAtTime(
      sustainGain,
      t + (preset.attackMs + preset.decayMs) / 1000,
    );
    this.amp.gain.exponentialRampToValueAtTime(0.0001, t + tailSec);

    this.osc1.connect(this.filter);
    this.osc2.connect(this.filter);
    this.filter.connect(this.amp);
    this.amp.connect(out);
    if (wetOut && preset.wet > 0) {
      const send = ctx.createGain();
      send.gain.value = preset.wet;
      this.amp.connect(send);
      send.connect(wetOut);
    }

    this.osc1.start(t);
    this.osc2.start(t);
    this.osc1.stop(t + stopSec);
    this.osc2.stop(t + stopSec);

    this.osc2.onended = () => {
      this.stopped = true;
      try {
        this.osc1.disconnect();
        this.osc2.disconnect();
        this.filter.disconnect();
        this.amp.disconnect();
      } catch {
        /* already disconnected */
      }
      this.onEnded?.();
    };
  }

  setOnEnded(cb: () => void): void {
    this.onEnded = cb;
  }

  /**
   * Glide to a new pitch for bend. Uses `setTargetAtTime` (exponential
   * approach with time constant `ms`) instead of a scheduled linear ramp —
   * that way the pitch curve is continuous across frames, even at 30 fps
   * tracking, and rapid target updates (vibrato) interpolate smoothly
   * instead of stepping every frame.
   */
  glideToCents(cents: number, ms = AUDIO.BEND.GLIDE_MS): void {
    if (this.stopped) return;
    if (!Number.isFinite(cents)) return;
    const t = this.ctx.currentTime;
    const rawTarget = this.baseFreq * Math.pow(2, cents / 1200);
    if (!Number.isFinite(rawTarget) || rawTarget <= 0) return;
    // Clamp to nominal audio range. Without this, a stale/garbage bend
    // value (e.g. cents tens of thousands) asks Web Audio to set a
    // frequency of ~1e18 Hz, which spams a warning and is clamped
    // anyway. Staying within a musically reasonable window (20 Hz to
    // Nyquist-ish) is always correct for a plucked string.
    const target = Math.min(23000, Math.max(20, rawTarget));
    const tau = Math.max(0.001, ms / 1000);
    this.osc1.frequency.cancelScheduledValues(t);
    this.osc2.frequency.cancelScheduledValues(t);
    this.osc1.frequency.setTargetAtTime(target, t, tau);
    this.osc2.frequency.setTargetAtTime(target * 1.002, t, tau);
  }

  /** Freeze the envelope at (at least) an audible level while the player
   *  holds a bend. Without this, bending a voice that has already decayed
   *  into the tail is inaudible — the pitch glide is technically applied
   *  but the gain is ~0. Called on bend-mode entry; `releaseBendHold`
   *  restarts the tail envelope when bend ends.
   *
   *  `minLevel` is the lowest gain we'll hold at (linear), so a very-soft
   *  original pluck doesn't suddenly blast out just because of a bend. */
  holdBendSustain(minLevel = 0.12, rampMs = 40): void {
    if (this.stopped || this.released) return;
    const t = this.ctx.currentTime;
    const cur = Math.max(0.0001, this.amp.gain.value);
    const target = Math.max(cur, minLevel);
    this.amp.gain.cancelScheduledValues(t);
    this.amp.gain.setValueAtTime(cur, t);
    this.amp.gain.exponentialRampToValueAtTime(target, t + rampMs / 1000);
  }

  /** Resume the natural tail after a bend hold. `tailMs` is how fast we
   *  fade back to inaudible — matches the preset's residual tail feel. */
  releaseBendHold(tailMs = 1800): void {
    if (this.stopped || this.released) return;
    const t = this.ctx.currentTime;
    const cur = Math.max(0.0001, this.amp.gain.value);
    this.amp.gain.cancelScheduledValues(t);
    this.amp.gain.setValueAtTime(cur, t);
    this.amp.gain.exponentialRampToValueAtTime(0.0001, t + tailMs / 1000);
  }

  /** Hard cut — fast fade-to-silence. Reserved for page-hide / dispose
   *  and polyphony voice-stealing where we need to reclaim the voice
   *  slot without waiting. For musical gestures like mute, use `release`
   *  so the tail fades through the ADSR-style shape instead of chopping. */
  cut(ms = 40): void {
    this.fadeOut(ms);
  }

  /** Musical release — long, soft fade so the tail decays through the
   *  ADSR-style envelope rather than being chopped. Use this at mute
   *  gesture edges so notes end the way the ear expects. */
  release(ms = 900): void {
    this.fadeOut(ms);
  }

  private fadeOut(ms: number): void {
    if (this.released || this.stopped) return;
    this.released = true;
    const t = this.ctx.currentTime;
    const dur = ms / 1000;
    const current = Math.max(0.0001, this.amp.gain.value);
    this.amp.gain.cancelScheduledValues(t);
    this.amp.gain.setValueAtTime(current, t);
    this.amp.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    try {
      this.osc1.stop(t + dur + 0.02);
      this.osc2.stop(t + dur + 0.02);
    } catch {
      /* already stopped */
    }
  }

  isAlive(): boolean {
    return !this.stopped;
  }
}
